// collection-data-hooks.ts
// The data attributes that mark an element, and what each one does with a value.

/**
 * An image or an uploaded file. Webflow gives one as an object carrying a
 * url, and the API passes it through whole. A gallery is a list of these,
 * and a list never arrives.
 */
export interface CmsAsset {
  url: string;
  alt?: string | null;
  fileId?: string;
}

/**
 * What the API sends back for one query. A bracketed path — `images[]` —
 * comes back as the whole gallery, an array of assets, and a reduction
 * comes back as a count no field holds.
 */
export type CmsResult = string | number | boolean | null | CmsAsset | CmsAsset[];

/** One result per query, keyed by the exact path the element carries. */
export type CmsResults = Record<string, CmsResult>;

/** A path the API could not resolve, and where it stopped. */
export interface Unresolved {
  path: string;
  reason: string;
  at?: string;
}

/** Where a resolved path's value came from, for the debug legend. */
export interface PathMeta {
  itemName?: string | null;
  fieldName?: string | null;
}

/** What one request cost, as the API measured it. */
export interface Timing {
  /** The whole request, inside the API. */
  ms: number;
  /** The part of it spent reading Webflow. */
  webflowMs: number;
  /** Reads that went out to Webflow. */
  reads: number;
  /** Reads a cached copy served instead. */
  hits: number;
}

export interface CollectionPayload {
  source?: string;
  slug?: string;
  cmsState?: string;
  values?: CmsResults;
  meta?: Record<string, PathMeta>;
  unresolved?: Unresolved[];
  timing?: Timing;
}

const SEPARATOR = '.';

/**
 * How many references a path walks before its field. `listing.main-image`
 * walks none, `listing.home-plan.page-second-image` walks one, and
 * `listing.home-plan.base-home-plan.secondary-image` walks two.
 */
export const depthOf = (path: string): number => Math.max(0, path.split(SEPARATOR).length - 2);

/**
 * The debug highlight of each depth, in the colors the internal gallery
 * documents use: blue for a field one reference deep, red for two. A field
 * of the page's own item gets none, so a highlighted image always came
 * through a reference. Each class carries the outline and its pulse, in
 * collection-data-legend.scss.
 */
const HIGHLIGHTS: Record<number, string> = {
  1: 'cms-debug-highlight--one-deep',
  2: 'cms-debug-highlight--two-deep'
};

export const highlightFor = (path: string): string | undefined => HIGHLIGHTS[depthOf(path)];

/** An asset is the only single value with a shape. */
export const isAsset = (value: CmsResult | undefined): value is CmsAsset =>
  Boolean(value)
  && typeof value === 'object'
  && !Array.isArray(value)
  && typeof (value as CmsAsset).url === 'string';

/** Quotes and backslashes would end the url() early. */
const cssUrl = (url: string): string => `url("${url.replace(/["\\]/g, '\\$&')}")`;

/** A line break in the CMS value becomes a line break on the page. */
const write = (node: HTMLElement, value: CmsResult): void => {
  // An asset or a gallery pointed at a text attribute would print junk.
  if (isAsset(value) || Array.isArray(value)) return;

  const [first, ...rest] = String(value).split('\n');

  node.textContent = first;
  rest.forEach((line) => node.append(document.createElement('br'), line));
};

/**
 * Swap the file an `img` shows.
 *
 * Webflow renders `srcset` and `sizes` beside `src`, and a browser prefers
 * the srcset candidate — so setting `src` alone leaves the old file on
 * screen. Both go.
 *
 * `alt` is written only when the CMS carries one, so a null never wipes alt
 * text somebody wrote by hand.
 */
const setImage = (node: HTMLElement, value: CmsResult): void => {
  if (!isAsset(value)) return;

  node.setAttribute('src', value.url);
  node.removeAttribute('srcset');
  node.removeAttribute('sizes');

  if (value.alt) node.setAttribute('alt', value.alt);
};

/** Swap the file an element shows behind its content. */
const setBackgroundImage = (node: HTMLElement, value: CmsResult): void => {
  if (!isAsset(value)) return;

  node.style.backgroundImage = cssUrl(value.url);
  // Without a background-size of its own the element shows the file at
  // natural size, tiled. Cover from the center is the treatment the LHG
  // tiles get from their class, so every target gets it inline.
  node.style.backgroundSize = 'cover';
  node.style.backgroundPosition = '50%';
};

/**
 * The classes of an item built from nothing, when an inject container holds
 * no child to clone. They are the Keylight gallery item's, hardcoded by
 * choice: one grid uses this today, and the styles amount to a rounded,
 * covering image.
 */
const FALLBACK_ITEM_CLASS = 'gl-image__item';
const FALLBACK_IMAGE_CLASS = 'image';

const buildItem = (): HTMLElement => {
  const item = document.createElement('div');
  const img = document.createElement('img');

  item.className = FALLBACK_ITEM_CLASS;
  img.className = FALLBACK_IMAGE_CLASS;
  img.setAttribute('loading', 'lazy');
  item.append(img);

  return item;
};

/**
 * Append one item per asset to the container carrying the attribute. A
 * gallery path sends
 * an array, and a single-image path sends one asset, which rides as a
 * gallery of one.
 *
 * The container's first child is the template, so an injected item wears
 * whatever the design gave its siblings. An empty container gets the
 * hardcoded item above — Webflow renders a gallery-less listing with
 * nothing to clone.
 *
 * Webflow also hides an empty grid (`w-dyn-hide`) and shows a "No items
 * found" sibling (`w-dyn-empty`). Injected images reverse both, so the
 * grid is seen exactly when it holds something.
 */
const injectImages = (
  node: HTMLElement,
  value: CmsResult,
  anchor?: ChildNode | null,
  highlight?: string
): void => {
  // The empty-state block is Webflow's, and it never takes images itself.
  if (node.classList.contains('w-dyn-empty')) return;

  const list = Array.isArray(value) ? value : [value];
  const assets = list.filter(isAsset);

  if (!assets.length) return;

  const template = node.firstElementChild as HTMLElement | null;

  assets.forEach((asset) => {
    const item = template ? (template.cloneNode(true) as HTMLElement) : buildItem();
    const img = item.tagName === 'IMG' ? item : item.querySelector('img');

    if (!img) return;

    setImage(img as HTMLElement, asset);
    // A clone of Webflow's ghost item keeps the class that hides it.
    img.classList.remove('w-dyn-bind-empty');
    // A cloned template may lack the sizing class the fallback carries.
    img.classList.add(FALLBACK_IMAGE_CLASS);
    // Each item wears the highlight of the entry that produced it, so one
    // container can hold depths apart.
    if (highlight) item.classList.add(highlight);
    // An entry written before `[~]` carries the container's original first
    // child as its anchor, and lands in front of it. No anchor appends.
    node.insertBefore(item, anchor ?? null);
  });

  node.classList.remove('w-dyn-hide');

  const empty = node.closest('.w-dyn-list')?.querySelector('.w-dyn-empty');

  if (empty) empty.classList.add('w-dyn-hide');
};

/** How long a swapped image takes to arrive. */
const FADE_MS = 300;

/**
 * Fetch the file before the swap, so the fade reveals a finished image
 * rather than one still arriving line by line.
 */
const preload = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    const probe = new Image();

    probe.onload = () => resolve(true);
    probe.onerror = () => resolve(false);
    probe.src = url;
  });

/**
 * Swap an element's image behind a fade: hide, swap, reveal. The whole
 * element fades, which is the point — the swap reads as arrival, not as
 * a flicker. A file that fails to load never swaps at all, and the
 * element keeps what Webflow shipped.
 *
 * Injected galleries stay out of this on purpose; their items enter with
 * the grid's own reveal.
 */
const fadeSwap = async (
  node: HTMLElement,
  value: CmsResult,
  swap: (node: HTMLElement, value: CmsResult) => void,
  highlight?: string
): Promise<void> => {
  if (!isAsset(value)) return;
  if (!(await preload(value.url))) return;

  node.style.transition = 'none';
  node.style.opacity = '0';
  swap(node, value);
  // Behind the same gate as the swap, so a file that never lands never
  // wears the highlight.
  if (highlight) node.classList.add(highlight);

  // The next frame, so the hidden swap paints before the fade begins.
  const frame = window.requestAnimationFrame
    ? window.requestAnimationFrame.bind(window)
    : (fn: () => void) => window.setTimeout(fn, 16);

  frame(() => {
    node.style.transition = `opacity ${FADE_MS}ms ease`;
    node.style.opacity = '1';
  });
};

/**
 * Every applier takes the element and the value. The anchor is only
 * imginject's business — the child its items land in front of. The
 * highlight is the `?__debug__` class of the path's depth, worn only by
 * what actually lands. An applier that works after a load returns a
 * promise, which `ready` awaits.
 */
type Applier = (
  node: HTMLElement,
  value: CmsResult,
  anchor?: ChildNode | null,
  highlight?: string
) => void | Promise<void>;

/**
 * One applier per attribute. A new entry here gives the markup a new
 * attribute and needs no other change.
 */
export const APPLIERS: Record<'text' | 'img' | 'bgimg' | 'imginject', Applier> = {
  text: write,
  img: (node, value, _anchor, highlight) => fadeSwap(node, value, setImage, highlight),
  bgimg: (node, value, _anchor, highlight) =>
    fadeSwap(node, value, setBackgroundImage, highlight),
  imginject: injectImages
};

export type ApplierName = keyof typeof APPLIERS;

export const APPLIER_NAMES = Object.keys(APPLIERS) as ApplierName[];

/** The attribute that names each applier. */
const ATTRIBUTES: Record<ApplierName, string> = {
  text: 'data-cms-ref',
  img: 'data-cms-img',
  bgimg: 'data-cms-bgimg',
  imginject: 'data-cms-imginject'
};

/**
 * The token that stands for the container's own markup in an imginject
 * list. Entries written before it land in front of what the page already
 * holds; entries after it land behind. Without it everything lands behind,
 * which is the same thing as writing it first.
 */
const CURRENT_CONTENT = '[~]';

export interface AttributeEntry {
  path: string;
  /** Insert in front of the container's original content, not behind it. */
  before: boolean;
}

/**
 * An imginject attribute carries a comma-separated list of paths, applied
 * in the order written. Every other attribute holds exactly one.
 */
export const entriesOf = (applier: ApplierName, raw: string): AttributeEntry[] => {
  if (applier !== 'imginject') return [{ path: raw, before: false }];

  const entries = raw.split(',').map((entry) => entry.trim());
  const split = entries.indexOf(CURRENT_CONTENT);

  return entries
    .map((path, at) => ({ path, before: split !== -1 && at < split }))
    .filter((entry) => entry.path !== CURRENT_CONTENT);
};

export const attributeOf = (name: ApplierName): string => ATTRIBUTES[name];

/** A first segment, with any selectors: `listing`, `listings[a=b][c="d"]`. */
const SOURCE_HEAD = /^([a-z0-9-]+)(\[[^\]]*\])*$/;

/**
 * An attribute holds a whole path: `source.reference.reference.field`, for
 * example `listing.home-plan.base-home-plan.beds-2` — or a collection
 * query, `listings[neighborhood-link=neighborhood]:length`.
 *
 * The path is kept whole, because the result comes back under the very string
 * the element carries. Only the source alias is pulled off it, to decide which
 * request this element's value rides in.
 */
export const readPath = (raw: string): { source: string; path: string } | null => {
  const path = (raw || '').trim();

  // The head ends at the first dot or colon outside the brackets — a
  // selector's quoted literal may carry either mark inside them.
  let end = path.length;
  let depth = 0;

  for (let at = 0; at < path.length; at += 1) {
    const mark = path[at];

    if (mark === '[') depth += 1;
    else if (mark === ']') depth -= 1;
    else if (depth === 0 && (mark === SEPARATOR || mark === ':')) {
      end = at;
      break;
    }
  }

  // A path needs a source and something read off it — a field, or a
  // reduction — so the separator cannot open or close the string.
  if (end < 1 || end >= path.length - 1) return null;

  const head = SOURCE_HEAD.exec(path.slice(0, end));

  if (!head) return null;

  return { source: head[1], path };
};

/**
 * The attributes a context pair rides on, split in two because Webflow
 * binds an attribute's whole value or none of it: the alias is typed by
 * hand, and the slug is bound to the CMS.
 *
 *     <div data-cms-context="neighborhood" data-cms-item="islandview">
 *
 * Both sit on the same element, paired positionally — a comma-separated
 * alias list pairs with a comma-separated slug list.
 */
export const CONTEXT_ATTRIBUTE = 'data-cms-context';
export const ITEM_ATTRIBUTE = 'data-cms-item';

const CONTEXT_REF = /\[[a-z0-9-]+=([a-z0-9-]+)\]/g;

/**
 * The context aliases a path's selectors name: an unquoted `[field=value]`
 * names the value as an alias. A quoted value is a literal — the API reads
 * it by the field's type — and it names nothing here.
 */
export const contextRefsOf = (path: string): string[] => {
  const refs: string[] = [];
  let match: RegExpExecArray | null;

  CONTEXT_REF.lastIndex = 0;
  while ((match = CONTEXT_REF.exec(path))) refs.push(match[1]);

  return refs;
};

/** What one element resolves a context alias to, or null when nothing declares it. */
export type ContextLookup = (element: HTMLElement, alias: string) => string | null;

/** The alias-to-slug pairs one context element declares, read positionally. */
const pairsOf = (element: HTMLElement): Record<string, string> => {
  const aliases = (element.getAttribute(CONTEXT_ATTRIBUTE) || '').split(',');
  const slugs = (element.getAttribute(ITEM_ATTRIBUTE) || '').split(',');
  const pairs: Record<string, string> = {};

  aliases.forEach((raw, at) => {
    const alias = raw.trim();
    const slug = (slugs[at] || '').trim();

    if (alias && slug) pairs[alias] = slug;
  });

  return pairs;
};

/**
 * The page's context, collected once: every context element in document
 * order, with its pairs read up front. Resolution walks that list
 * backward — document order puts an ancestor before its descendants, so
 * the last containing one is the nearest — and no element ever walks the
 * tree itself. A page with one context element skips even the containment
 * check: that one governs every replacement on the page.
 */
export const buildContext = (root: ParentNode = document): ContextLookup => {
  const elements = Array.from(root.querySelectorAll<HTMLElement>(`[${CONTEXT_ATTRIBUTE}]`));
  const pairs = elements.map(pairsOf);
  const single = elements.length === 1;

  return (element, alias) => {
    for (let at = elements.length - 1; at >= 0; at -= 1) {
      const value = pairs[at][alias];

      if (value && (single || elements[at].contains(element))) return value;
    }

    return null;
  };
};

/** A value no applier writes. The element then keeps what the design shipped. */
export const isEmpty = (value: CmsResult | undefined): boolean => {
  if (value === undefined || value === null || value === '') return true;

  // A gallery with nothing in it injects nothing.
  if (Array.isArray(value)) return !value.length;

  // An unset image can arrive as an object with nothing on it.
  if (typeof value === 'object') return !isAsset(value) || !value.url;

  return false;
};
